'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface UseUnsavedChangesGuardOptions {
  /** Opt-in — pass the caller's own toggle (e.g. `confirmUnsavedChanges`). */
  enabled: boolean;
  /**
   * Identity of the item currently shown in the drawer. The drawer itself
   * isn't remounted between items (`selectedItem`/`selectedContact` isn't
   * cleared to null on close), so without this, `isDirty` from one item's
   * session would leak into the next item's — showing the discard prompt
   * with no actual edits made on the newly-opened item. Resetting is done
   * during render (React's documented pattern for resetting state on a prop
   * change), not in an effect.
   */
  resetKey: string | number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface UnsavedChangesGuard {
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  showUnsavedDialog: boolean;
  setShowUnsavedDialog: (show: boolean) => void;
  /** Pass as the `Sheet`'s `onOpenChange` — intercepts a dirty close. */
  handleOpenChange: (nextOpen: boolean) => void;
  /** Wire to the discard dialog's destructive action. */
  handleDiscardChanges: () => void;
}

/**
 * Shared "warn before losing unsaved edits" behaviour for a single-item
 * edit-first drawer — see `SimpleInventoryDrawer` (LF-5542) and
 * `ContactDetailsDrawer` (LF-5547), which both need the identical: dirty
 * tracking reset-on-item-change, a `beforeunload` listener, and interception
 * of an explicit drawer close while dirty.
 *
 * Deliberately keyed off the caller's own genuine-user-edit signal (passed
 * in via `setIsDirty`, wired to the form card's `onDirtyChange`) rather than
 * computing dirtiness itself — see `InventoryFormCard`'s `DirtyStateReporter`
 * doc comment for why raw RHF `isDirty` is the wrong signal (it flips true
 * for legitimate programmatic auto-fills with no user input).
 */
export function useUnsavedChangesGuard({
  enabled,
  resetKey,
  open,
  onOpenChange
}: UseUnsavedChangesGuardOptions): UnsavedChangesGuard {
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  /**
   * **`handleOpenChange` reads this, not `isDirty`** (VALE-349).
   *
   * The attribute treatment can turn dirty *inside the very dispatch that is
   * closing the drawer*: Radix's dismissable layer detects an outside click on
   * `pointerdown`, the drawer forces the open field's commit-on-blur in that
   * same dispatch (VALE-60), and a value the commit path refuses becomes unsaved
   * state before Radix dismisses — but after `handleOpenChange` closed over
   * `isDirty`. A ref written beside the state is the same trick
   * `useAttributeCommit` uses on its draft, and for the same reason: the answer
   * has to be right before any render.
   */
  const isDirtyRef = useRef(false);
  const writeIsDirty = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty;
    setIsDirty(dirty);
  }, []);

  const [renderedKey, setRenderedKey] = useState(resetKey);
  if (resetKey !== renderedKey) {
    setRenderedKey(resetKey);
    setIsDirty(false);
    setShowUnsavedDialog(false);
  }

  /*
    The mirror of the reset above. It is an effect rather than a line beside it
    because a ref must not be written during render — and it costs nothing:
    effects flush before the next gesture, so `handleOpenChange` can never read
    the previous item's answer.
  */
  useEffect(() => {
    isDirtyRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    if (!enabled || !isDirty || !open) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled, isDirty, open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && enabled && isDirtyRef.current) {
      setShowUnsavedDialog(true);
      return;
    }
    onOpenChange(nextOpen);
  };

  const handleDiscardChanges = () => {
    setShowUnsavedDialog(false);
    // `writeIsDirty`, not the raw setter: discarding is a decision the *next*
    // close has to see, and `handleOpenChange` reads the ref.
    writeIsDirty(false);
    onOpenChange(false);
  };

  return {
    isDirty,
    setIsDirty: writeIsDirty,
    showUnsavedDialog,
    setShowUnsavedDialog,
    handleOpenChange,
    handleDiscardChanges
  };
}

export interface UnsavedChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDiscard: () => void;
  /** Noun describing what's being discarded, e.g. "item", "contact". */
  itemLabel: string;
}

/** The discard-confirmation dialog paired with `useUnsavedChangesGuard`. */
export function UnsavedChangesDialog({
  open,
  onOpenChange,
  onDiscard,
  itemLabel
}: UnsavedChangesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Discard unsaved changes?</DialogTitle>
          <DialogDescription>
            You have unsaved changes to this {itemLabel}. If you close now, they
            will be lost.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Keep editing
          </Button>
          <Button variant="destructive" onClick={onDiscard}>
            Discard changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
